import type { ActivityFeed, FeedRow, OpRow } from "../app/activity-model.js";
import type { ScheduledSection } from "../app/mandates-model.js";
import { scheduleRow } from "./scheduled.js";
import { el } from "../lib/dom.js";
import { keepTogether } from "../lib/keep.js";
import { icon } from "../lib/icon.js";
import type { AimHandlers } from "./aim.js";
import { tabBar, type Tab } from "./tabs.js";

/**
 * 11 Activity (UX spec v3 §6.13, mock-up 11-activity.html): filters that wrap,
 * days with the money that left, and rows — never a table. Inside `.feed` a row
 * WRAPS, never clips (the system's own rule for the statement: DCTA-67's clipped
 * source account must not recur). A bundle is one row that expands in place
 * (`aria-expanded`, its operations in `.subs` under it); every operation and line is a
 * button that opens its receipt. DCTA-166: only what the customer signed is here — the
 * model makes no row for anything that was not (activity-model.ts).
 *
 * States: loading (a skeleton of the same heights, no text), list, filter, empty
 * (one block, no button — the assistant's mark is the way to start), and unreachable
 * (the banner and Retry; what was on screen stays).
 */
export interface ActivityHandlers {
  /** A row's receipt — or (DCTA-209) `mandate:{id}`, a schedule: a Scheduled row, a "Set up" row. */
  onOpen(key: string): void;
  /** DCTA-209 (28a): the section's "See all {n}" — the Scheduled list. */
  onSeeAll?(): void;
  onFilter(account: string): void;
  onRetry(): void;
  onTab(tab: Tab): void;
  readonly assistant: AimHandlers;
}

export class ActivityView {
  readonly parts: readonly HTMLElement[];
  readonly body: HTMLElement;
  readonly topbar: HTMLElement;
  readonly dock: HTMLElement;
  readonly mark: HTMLButtonElement;
  readonly #h: ActivityHandlers;
  readonly #filters: HTMLElement;
  readonly #feed: HTMLElement;
  readonly #skel: HTMLElement;
  readonly #empty: HTMLElement;
  readonly #banner: HTMLElement;
  readonly #open = new Set<string>();
  /** DCTA-209 (F57, 28a): "Scheduled" at the top — at most three rows, soonest first; gone when nothing is scheduled. */
  readonly #sched: HTMLElement;
  readonly #schedRows: HTMLElement;
  readonly #seeAll: HTMLButtonElement;
  /** What had focus when a layer opened over Activity (the Scheduled layer), to give it back when Activity is drawn again. */
  #focusMark: { readonly all: boolean; readonly key?: string; readonly mandate?: string } | undefined;

  constructor(handlers: ActivityHandlers) {
    this.#h = handlers;
    this.topbar = el("div", { class: "topbar" }, [
      el("span", { class: "topbar-pad", aria: { hidden: "true" } }),
      el("span", { class: "t" }, ["Activity"]),
      el("span", { class: "topbar-pad", aria: { hidden: "true" } }),
    ]);
    const retry = el("button", { class: "raw", type: "button" }, ["Retry"]);
    retry.addEventListener("click", () => this.#h.onRetry());
    this.#banner = el("div", { class: "banner", role: "status", hidden: true }, [
      el("span", { class: "bic", aria: { hidden: "true" } }, [icon("i-alert")]),
      el("span", {}, ["Can't reach the bank right now. Nothing has changed."]),
      retry,
    ]);
    this.#filters = el("div", { class: "filters", role: "group", aria: { label: "Show" } });
    this.#feed = el("div", { class: "feed" });
    this.#skel = el("div", { class: "skel", aria: { hidden: "true" }, hidden: true }, [
      el("span", { class: "sk sk-dayline" }),
      el("div", { class: "list" }, [el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" })]),
      el("span", { class: "sk sk-dayline" }),
      el("div", { class: "list" }, [el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" })]),
    ]);
    this.#empty = el("div", { class: "empty", hidden: true }, [
      el("span", { class: "ic", aria: { hidden: "true" } }, [icon("i-list")]),
      el("b", {}, ["Nothing yet"]),
      el("p", {}, ["What you do shows up here, with a receipt for each one."]),
    ]);
    this.#schedRows = el("div", { class: "list feed sch", role: "list", aria: { labelledby: "act-sched-h" } });
    this.#seeAll = el("button", { class: "raw all", type: "button" });
    this.#seeAll.addEventListener("click", () => this.#h.onSeeAll?.());
    this.#sched = el("div", { class: "schsec", hidden: true }, [
      el("div", { class: "sec" }, [el("h3", { id: "act-sched-h" }, ["Scheduled"]), this.#seeAll]),
      this.#schedRows,
    ]);
    this.body = el("div", { class: "body" }, [
      this.#banner,
      this.#filters,
      this.#sched,
      this.#feed,
      this.#skel,
      this.#empty,
      el("div", { class: "home-end", aria: { hidden: "true" } }),
    ]);
    const bar = tabBar("activity", { onTab: (t) => this.#h.onTab(t), ...this.#h.assistant });
    this.mark = bar.mark;
    this.dock = bar.dock;
    this.parts = [this.topbar, this.body, this.dock];
  }

  /** A bundle row drawn open (the Receipt pill's way in). */
  expand(bundleId: string): void {
    this.#open.add(bundleId);
  }

  scrollToBundle(bundleId: string): void {
    const head = this.#feed.querySelector<HTMLElement>(`.item.bundle[data-bundle="${CSS.escape(bundleId)}"]`);
    if (head === null) return;
    head.scrollIntoView({ block: "start" });
    head.focus({ preventScroll: true });
  }

  /** A layer opens over Activity: remember the control that opened it (a row's key or schedule, or See all). */
  markFocus(): void {
    const a = document.activeElement;
    if (!(a instanceof HTMLElement) || !this.body.contains(a)) {
      this.#focusMark = undefined;
      return;
    }
    const key = a.dataset["key"];
    const mandate = a.dataset["mandate"];
    this.#focusMark = { all: a === this.#seeAll, ...(key === undefined ? {} : { key }), ...(mandate === undefined ? {} : { mandate }) };
  }

  /**
   * Back from that layer, drawn again: focus on what opened it, wherever it now sits (WCAG 2.4.3). Gone (a stop took the
   * schedule out of the section): the same schedule's Set up row; else the filter chosen — never the page.
   */
  returnFocus(): void {
    const m = this.#focusMark;
    this.#focusMark = undefined;
    if (m === undefined) return;
    const by = (attr: string, v: string | undefined): HTMLElement | null => (v === undefined ? null : this.body.querySelector<HTMLElement>(`button[${attr}="${CSS.escape(v)}"]`));
    const target =
      (m.all && !this.#sched.hidden ? this.#seeAll : null) ??
      by("data-key", m.key) ??
      by("data-mandate", m.mandate) ??
      by("data-key", m.mandate === undefined ? undefined : `mandate:${m.mandate}`) ??
      this.#filters.querySelector<HTMLElement>("button[aria-pressed='true']");
    target?.focus({ preventScroll: true });
  }

  loading(): void {
    this.#skel.hidden = false;
    this.#empty.hidden = true;
    this.#sched.hidden = true;
    this.#feed.replaceChildren();
  }

  /**
   * DCTA-209 (F57, 28a): the Scheduled section — at most three rows soonest first and "See all {n}" when there is more; no
   * section at all when nothing is scheduled (for the account chosen).
   */
  scheduled(section: ScheduledSection | undefined): void {
    this.#sched.hidden = section === undefined;
    if (section === undefined) {
      this.#schedRows.replaceChildren();
      return;
    }
    this.#schedRows.replaceChildren(...section.rows.map((r) => scheduleRow(r, (id) => this.#h.onOpen(`mandate:${id}`))));
    this.#seeAll.hidden = section.seeAll === undefined;
    if (section.seeAll !== undefined) {
      this.#seeAll.replaceChildren(section.seeAll.label, icon("i-chev-r"));
      this.#seeAll.setAttribute("aria-label", section.seeAll.aria);
    }
  }

  show(feed: ActivityFeed, selected: string): void {
    this.#banner.hidden = true;
    this.#skel.hidden = true;
    const hadFocus = this.#filters.contains(document.activeElement);
    this.#filters.replaceChildren(
      ...feed.chips.map((c) => {
        const chip = el("button", { class: "raw chip", type: "button", aria: { pressed: c.id === selected ? "true" : "false" } }, [c.label]);
        chip.dataset["acc"] = c.id;
        chip.addEventListener("click", () => this.#h.onFilter(c.id));
        return chip;
      }),
    );
    if (hadFocus) this.#filters.querySelector<HTMLElement>(`[data-acc="${CSS.escape(selected)}"]`)?.focus({ preventScroll: true });
    this.#filters.hidden = feed.chips.length <= 1 && feed.days.length === 0;
    this.#empty.hidden = feed.days.length > 0 || !this.#sched.hidden;
    this.#feed.replaceChildren(
      ...feed.days.flatMap((day) => [
        el("div", { class: "day" }, [el("span", {}, [day.label]), ...(day.out === undefined ? [] : [el("span", { class: "money" }, [day.out])])]),
        el("div", { class: "list" }, day.rows.flatMap((row) => this.#row(row))),
      ]),
    );
  }

  /** The read failed: what is on screen stays; the banner says so and offers Retry. */
  unreachable(): void {
    this.#skel.hidden = true;
    this.#banner.hidden = false;
  }

  #row(row: FeedRow): HTMLElement[] {
    switch (row.kind) {
      case "bundle": {
        const open = this.#open.has(row.id);
        const head = el("button", { class: "raw item bundle", type: "button", aria: { expanded: open ? "true" : "false" } }, [
          el("span", { class: "ic", aria: { hidden: "true" } }, [icon("i-key")]),
          el("span", { class: "tx" }, [el("b", {}, [row.title]), el("small", {}, keepTogether(row.sub))]),
          ...(row.amount === "" ? [] : [el("span", { class: "amt money" }, [row.amount])]),
          icon("i-chev-d", "chev"),
        ]);
        head.dataset["bundle"] = row.id;
        head.addEventListener("click", () => {
          const now = head.getAttribute("aria-expanded") !== "true";
          head.setAttribute("aria-expanded", now ? "true" : "false");
          if (now) this.#open.add(row.id);
          else this.#open.delete(row.id);
        });
        return [head, el("div", { class: "subs" }, row.ops.map((op) => this.#op(op, op.sub, op.amount)))];
      }
      case "single":
        return [this.#op(row.op, row.sub, row.amount)];
      case "line":
        return [
          this.#button(row.key, el("span", { class: "ic", aria: { hidden: "true" } }, [row.initial]), row.title, row.sub, row.status, row.amount, {
            pos: row.credit,
            muted: row.muted,
          }),
        ];
      case "occurrence":
      case "setup": {
        // DCTA-209 (30a, 30e): a payment a schedule made or skipped, and the day it was set up — the bank's line after
        // "Scheduled ·", its word in bold; a skipped row and a set-up row are muted, with no minus.
        const tail = row.kind === "occurrence" && row.st !== undefined ? [el("span", { class: "st" }, [row.st]), row.rest ?? ""] : [];
        const button = el("button", { class: "raw item", type: "button" }, [
          el("span", { class: "ic", aria: { hidden: "true" } }, [icon(row.ic)]),
          el("span", { class: "tx" }, [el("b", {}, [row.title]), el("small", {}, [...keepTogether(row.sub), ...tail])]),
          ...(row.amount === "" ? [] : [el("span", { class: `amt money${row.muted ? " muted" : ""}` }, [row.amount])]),
        ]);
        button.dataset["key"] = row.key;
        button.addEventListener("click", () => this.#h.onOpen(row.key));
        return [button];
      }
    }
  }

  #op(op: OpRow, sub: string, amount: string): HTMLElement {
    return this.#button(op.key, el("span", { class: "ic", aria: { hidden: "true" } }, [icon(op.ic)]), op.title, sub, op.status, amount, {
      pos: false,
      muted: op.muted,
    });
  }

  #button(
    key: string,
    ic: HTMLElement,
    title: string,
    sub: string,
    status: string | undefined,
    amount: string,
    look: { readonly pos: boolean; readonly muted: boolean },
  ): HTMLElement {
    // P1-6 (DCTA-192): a date, a time or a count in the sub-line never breaks across lines ("1 held").
    const small = el("small", {}, [...keepTogether(sub), ...(status === undefined ? [] : [sub === "" ? "" : " · ", el("span", { class: "st" }, [status])])]);
    const button = el("button", { class: "raw item", type: "button" }, [
      ic,
      el("span", { class: "tx" }, [el("b", {}, [title]), small]),
      ...(amount === "" ? [] : [el("span", { class: `amt money${look.pos ? " pos" : ""}${look.muted ? " muted" : ""}` }, [amount])]),
    ]);
    button.dataset["key"] = key;
    button.addEventListener("click", () => this.#h.onOpen(key));
    return button;
  }
}
